import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page TournamentListPage
 * @description 賽事列表（UI-2-1）
 * 顯示所有賽事，支援狀態篩選、建立跳轉
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, apiClient } from '../../lib/api.ts';
import { safeFormatDate } from '../../lib/format';
import type { TournamentDto, TournamentStatus } from '@pokerroom/types';
import { formatAmount, TournamentStatusBadge } from './tournamentDisplay';

const PAGE_SIZE = 20;

const ALL_STATUSES: TournamentStatus[] = [
  'DRAFT',
  'REGISTERED',
  'IN_PROGRESS',
  'PAUSED',
  'COMPLETED',
  'CANCELLED',
];

async function downloadTournamentCsv(id: string) {
  const res = await apiClient.get(`/tournament/${id}/export`, { responseType: 'blob' });
  const disposition = res.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? `tournament-${id.slice(0, 8)}.csv`;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const Spinner = () => (
  <span
    aria-hidden="true"
    className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent"
  />
);

export default function TournamentListPage() {
  const { t, i18n } = useTranslation('tournament');
  const [statusFilter, setStatusFilter] = useState<TournamentStatus | undefined>();
  const [page, setPage] = useState(1);
  const [exportErrorId, setExportErrorId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<{
    data: TournamentDto[];
    total: number;
  }>({
    queryKey: ['tournaments', statusFilter, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter) params.set('status', statusFilter);
      return api.get(`/tournament?${params}`);
    },
    placeholderData: (prev) => prev,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const tournaments = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const locale = i18n.language;

  const selectStatus = (s: TournamentStatus | undefined) => {
    setStatusFilter(s);
    setPage(1);
  };

  const exportCsv = async (id: string) => {
    setExportErrorId(null);
    try {
      await downloadTournamentCsv(id);
    } catch {
      // The auth interceptor still handles 401; any failure is shown on the row.
      setExportErrorId(id);
    }
  };

  const filterButton = (s: TournamentStatus | undefined, label: string) => {
    const pressed = statusFilter === s;
    return (
      <button
        key={s ?? 'all'}
        type="button"
        aria-pressed={pressed}
        onClick={() => selectStatus(s)}
        className={`min-h-10 max-md:min-h-11 px-3 text-sm font-medium border transition-colors ${
          pressed
            ? 'bg-primary border-primary text-primary-content'
            : 'border-base-300 text-base-content hover:bg-base-300'
        }`}
      >
        {label}
      </button>
    );
  };

  const outlineAction =
    'inline-flex min-h-10 max-md:min-h-11 items-center px-4 border border-base-300 text-sm font-medium text-base-content hover:bg-base-300 transition-colors';

  return (
    <AdminPage className="admin-page-body">
      {/* Header */}
      <AdminPageHeader
        title={t('title')}
        actions={
          <>
            <Link
              to="/tournament/create"
              className="inline-flex min-h-10 max-md:min-h-11 items-center px-4 bg-primary text-primary-content font-medium hover:bg-primary/90 transition-colors"
            >
              {t('create')}
            </Link>
          </>
        }
      />

      {/* 狀態篩選 */}
      <div role="group" aria-label={t('statusFilter')} className="flex flex-wrap gap-2 mb-6">
        {filterButton(undefined, t('all'))}
        {ALL_STATUSES.map((s) => filterButton(s, t(`status.${s}`)))}
      </div>

      {/* 賽事列表 */}
      {isError ? (
        <div role="alert" className="py-12 text-center space-y-3">
          <p className="text-base text-base-content">{t('loadError')}</p>
          <button type="button" onClick={() => void refetch()} className={outlineAction}>
            {t('retry')}
          </button>
        </div>
      ) : isLoading ? (
        <p
          role="status"
          className="flex items-center justify-center gap-2 py-12 text-base-content/70"
        >
          <Spinner />
          {t('loading')}
        </p>
      ) : tournaments.length === 0 ? (
        <div className="py-12 text-center space-y-4 bg-base-200 border border-base-300">
          <p className="text-base text-base-content">
            {statusFilter ? t('noTournamentsInStatus') : t('noTournamentsYet')}
          </p>
          <Link to="/tournament/create" className={outlineAction}>
            {t('createFirst')}
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {tournaments.map((item) => {
            const canExport = item.status === 'COMPLETED' || item.status === 'CANCELLED';
            return (
              <li key={item.id} className="bg-base-200 border border-base-300">
                <div className="flex items-stretch">
                  <Link
                    to={`/tournament/${item.id}`}
                    className="flex flex-1 min-w-0 items-center gap-3 p-4 sm:p-5 hover:bg-base-300 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h2 className="text-base font-semibold text-base-content break-words min-w-0">
                          {item.name}
                        </h2>
                        <TournamentStatusBadge status={item.status} />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-base-content/70 tabular-nums [&>span]:whitespace-nowrap">
                        <span>{t('buyinLabel', { amount: formatAmount(item.buyin, locale) })}</span>
                        <span aria-hidden="true">·</span>
                        <span>
                          {t('prizePoolLabel', { amount: formatAmount(item.prizePool, locale) })}
                        </span>
                        {item._count?.entries != null && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>{t('entryCountShort', { count: item._count.entries })}</span>
                          </>
                        )}
                        <span aria-hidden="true">·</span>
                        <span>
                          {safeFormatDate(item.startedAt ?? item.createdAt, (d) =>
                            d.toLocaleDateString(locale),
                          )}
                        </span>
                      </div>
                    </div>
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 16 16"
                      className="h-4 w-4 shrink-0 text-base-content/70"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M6 3l5 5-5 5" />
                    </svg>
                  </Link>
                  {canExport && (
                    <div className="flex items-center border-l border-base-300 px-3">
                      <button
                        type="button"
                        onClick={() => void exportCsv(item.id)}
                        className="relative inline-flex min-h-10 max-md:min-h-11 items-center gap-2 px-3 border border-base-300 text-sm text-base-content hover:bg-base-300 transition-colors"
                      >
                        {t('exportCsv')}
                        <span className="sr-only">{item.name}</span>
                      </button>
                    </div>
                  )}
                </div>
                {exportErrorId === item.id && (
                  <p role="alert" className="border-t border-base-300 px-4 py-2 text-sm text-error">
                    {t('exportFailed')}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* 分頁 */}
      {totalPages > 1 && !isError && (
        <nav
          aria-label={t('pagination')}
          className="flex flex-wrap items-center justify-between gap-3 mt-6"
        >
          <p className="text-sm text-base-content/70 tabular-nums">
            {t('rangeSummary', {
              from: (page - 1) * PAGE_SIZE + 1,
              to: Math.min(page * PAGE_SIZE, total),
              total,
            })}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="min-h-10 max-md:min-h-11 px-3 border border-base-300 text-sm text-base-content disabled:text-base-content/50 disabled:cursor-not-allowed enabled:hover:bg-base-300"
            >
              {t('prevPage')}
            </button>
            <span className="px-2 text-sm text-base-content/70 tabular-nums">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="min-h-10 max-md:min-h-11 px-3 border border-base-300 text-sm text-base-content disabled:text-base-content/50 disabled:cursor-not-allowed enabled:hover:bg-base-300"
            >
              {t('nextPage')}
            </button>
          </div>
        </nav>
      )}
    </AdminPage>
  );
}
